import { useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Controller,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useLocalStorage } from 'usehooks-ts';

import {
  getLoanOfferRoute,
  refreshTokens,
  useSubmitApplication,
} from '@consumer-frontend/nc-api';
import {
  applicationDecisions,
  confirmMinLengthError,
  initialAlertBannerProps,
  isDefined,
  LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
  MIN_PASSWORD_LENGTH,
  minLengthError,
  minLengthHelperText,
  NcBorrowingNeedsFormT,
  passwordMatchError,
  redirectTo,
  somethingWentWrongAlertBannerProps,
  STATE_LOCAL_STORAGE_KEY,
  StateValueT,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { useApplyRouting } from '../../routing';
import {
  alertBannerAtom,
  doesPasswordExistAtom,
  incomeDataAtom,
  iovationBlackboxAtom,
  iovationPurchasedAtom,
  isCaliforniaAddressAtom,
  isReturningCustomerFlowAtom,
} from '../../store';
import { NcButton } from '../NcButton';
import { consentRequiredError, NcConsents } from '../NcConsents';
import { NcForm } from '../NcForm';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';
import { NcPassword } from '../NcPassword';
import { NcProgressBar } from '../NcProgressBar';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/consents-page.scss';

type ConsentsValuesT = {
  consentBundleChecked: boolean;
  delawareConsentChecked?: boolean;
};

type ConsentsFormValuesT = {
  confirmPassword: string;
  consents: ConsentsValuesT;
  password: string;
};

export const NcConsentsPage: React.FC = () => {
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const setIncomeData = useSetAtom(incomeDataAtom);

  const isCalifornia = useAtomValue(isCaliforniaAddressAtom);
  const isReturningCustomerFlow = useAtomValue(isReturningCustomerFlowAtom);
  const doesPasswordExist = useAtomValue(doesPasswordExistAtom);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const { handleBack, progress } = useApplyRouting();

  // Runs on every render; CodeCov mis-flags the closing line of this multiline useLocalStorage call.
  // istanbul ignore next -- @preserve
  const [
    addressState,
    _setAddressState,
    removeAddressState,
  ] = useLocalStorage<StateValueT | ''>(STATE_LOCAL_STORAGE_KEY, '');

  const isDelaware = addressState === 'DE';

  // Runs on every render; CodeCov mis-flags the closing line of this multiline useLocalStorage call.
  // istanbul ignore next -- @preserve
  const [
    loanAmountAndPurpose,
    _storeLoanAmountAndPurpose,
    removeLoanAmountAndPurpose,
  ] = useLocalStorage<Partial<NcBorrowingNeedsFormT>>(LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY, {});
  const { loanAmount, loanPurpose } = loanAmountAndPurpose;

  // Runs on every render; CodeCov mis-flags the closing line of this multiline useForm call.
  // istanbul ignore next -- @preserve
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = useForm<ConsentsFormValuesT>({
    defaultValues: {
      confirmPassword: '',
      consents: { consentBundleChecked: false },
      password: '',
    },
  });
  const passwordValue = watch('password');
  const isConsentBundleChecked = watch('consents.consentBundleChecked');
  const isDelawareConsentChecked = watch('consents.delawareConsentChecked');

  const { isMutating, submitApplication } = useSubmitApplication(
    {
      isConsentBundleChecked,
      isDelawareConsentChecked,
      isReturningCustomerFlow,
      loanAmount,
      loanPurpose,
      password: passwordValue,
    },
    iovationBlackboxAtom,
    iovationPurchasedAtom,
  );

  const header = doesPasswordExist ? 'Agree and Submit' : 'Create Password';

  // Log when the consents page is visited with a password already set (i.e., returning customer flow).
  useEffect(() => {
    if (doesPasswordExist) {
      datadogRum.addAction('consentsPageVisitedWithPassword');
    }
  }, [doesPasswordExist]);

  const onSubmit: SubmitHandler<ConsentsFormValuesT> = async () => {
    setAlertBanner(initialAlertBannerProps); // Clear alert from any previous error.
    let resp;
    let isIovationPurchaseSuccessful;

    try {
      const result = await submitApplication();

      resp = result.response;
      isIovationPurchaseSuccessful = result.isIovationPurchaseSuccessful;
    } catch (error) {
      datadogRum.addAction('applicationSubmissionError', {
        // @ts-expect-error - TypeScript only allows `error` to be `any` or `unknown` in catch blocks 🙄
        error: error?.message ?? `Unknown error: ${error}`,
      });

      setAlertBanner(somethingWentWrongAlertBannerProps);

      return;
    }

    if (!isIovationPurchaseSuccessful) {
      setAlertBanner(somethingWentWrongAlertBannerProps);

      return;
    }

    if (resp?.status === 201 && isDefined(resp?.decision)) {
      if (!applicationDecisions.includes(resp.decision)) {
        try {
          datadogRum.addAction('applicationDecisionUnsupported', { decision: resp.decision });
        } catch {
          // No logging or action necessary, just ensuring that a DD RUM error here doesn't prevent the user from continuing.
        }

        setAlertBanner(somethingWentWrongAlertBannerProps);

        return;
      }

      datadogRum.addAction('applicationDecision', { decision: resp.decision });

      try {
        await refreshTokens();
      } catch (error) {
        datadogRum.addAction('tokenRefreshAfterApplicationSubmissionFailed', {
          // @ts-expect-error - TypeScript only allows `error` to be `any` or `unknown` in catch blocks 🙄
          error: error?.message ?? `Unknown error: ${error}`,
        });

        setAlertBanner(somethingWentWrongAlertBannerProps);

        return;
      }

      setIsRedirecting(true);

      // Clear all data related to the application process.
      removeAddressState();
      removeLoanAmountAndPurpose();
      setIncomeData({});

      redirectTo(getLoanOfferRoute(resp.decision));
    }
  };

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-consents-page" hasBoxShadow>
        <NcProgressBar progress={progress} />

        <h1 className="h3">{header}</h1>

        {!doesPasswordExist && (
          <p>
            {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
            {"You'll use this password to sign in to your NetCredit account."}
          </p>
        )}

        <NcForm className="consents-form" onSubmit={handleSubmit(onSubmit)}>
          {!doesPasswordExist && (
            <div className="passwords">
              <NcPassword
                {...register('password', {
                  minLength: {
                    message: minLengthError,
                    value: MIN_PASSWORD_LENGTH,
                  },
                  required: minLengthError,
                })}
                autoComplete="new-password"
                errorMessage={errors.password?.message}
                helperText={minLengthHelperText}
                id="password"
                label="Password"
              />

              <NcPassword
                {...register('confirmPassword', {
                  deps: ['password'],
                  minLength: {
                    message: confirmMinLengthError,
                    value: MIN_PASSWORD_LENGTH,
                  },
                  required: confirmMinLengthError,
                  validate: (value) => value === passwordValue || passwordMatchError,
                })}
                autoComplete="new-password"
                errorMessage={errors.confirmPassword?.message}
                id="confirmPassword"
                label="Confirm Password"
              />
            </div>
          )}

          <Controller
            control={control}
            name="consents"
            rules={{
              validate: (value) => {
                if (!value?.consentBundleChecked) return consentRequiredError;
                if (isDelaware && !value?.delawareConsentChecked) return consentRequiredError;

                return true;
              },
            }}
            render={({ field }) => (
              // NcConsents, its error ternaries, and change handlers are exercised by the validation/happy stories; flagged due to JSX source-map drift.
              <NcConsents
                consentBundleChecked={!!field.value.consentBundleChecked}
                consentBundleError={!field.value?.consentBundleChecked ? errors.consents?.message : undefined}
                delawareConsentChecked={!!field.value.delawareConsentChecked}
                delawareConsentError={
                  isDelaware && !field.value?.delawareConsentChecked ? errors.consents?.message : undefined
                }
                isCalifornia={isCalifornia}
                isDelaware={isDelaware}
                onConsentBundleChange={(e) => field.onChange({ ...field.value, consentBundleChecked: e.target.checked })}
                onDelawareConsentChange={(e) => field.onChange({ ...field.value, delawareConsentChecked: e.target.checked })}
              />
            )}
          />

          <Fieldset className="cta-buttons">
            <NcButton
              loading={isSubmitting || isMutating || isRedirecting}
              type="submit"
              variant="primary"
            >
              View Offer
            </NcButton>

            <NcButton
              disabled={isSubmitting || isMutating || isRedirecting}
              onClick={handleBack}
              type="button"
              variant="outline-primary"
            >
              Back
            </NcButton>
          </Fieldset>
        </NcForm>
      </Container>
    </NcPageWithAlertBanner>
  );
};
